import { useState, useEffect } from "react";
import { devError } from "@/lib/logger";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { seasonService } from "@/services";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Award, Star, Lock, Unlock, Palette, Check, Zap } from "lucide-react";
import AccountNavMenu from "@/components/AccountNavMenu";
import SeasonCountdown from "@/components/SeasonCountdown";

export default function SeasonPassPage() {
  const { t } = useTranslation();
  usePageTitle("Season Pass");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [progress, setProgress] = useState(null);
  const [cardBacks, setCardBacks] = useState([]);
  const [activeBack, setActiveBack] = useState("default");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      seasonService.getCurrentSeason(),
      seasonService.getProgress(),
      seasonService.getCardBacks(),
    ]).then(([s, p, cb]) => {
      setSeason(s);
      setProgress(p);
      setCardBacks(cb.card_backs || []);
      setActiveBack(cb.active || "default");
      setLoading(false);
    }).catch(err => { devError("Failed to load season pass:", err); setLoading(false); });
  }, [user]);

  const setCardBack = async (backId) => {
    try {
      await seasonService.setCardBack(backId);
      setActiveBack(backId);
    } catch {
      // Card back change failed — log and revert UI state
      devError("Failed to set card back");
    }
  };

  if (loading) return <div className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] flex items-center justify-center"><div className="animate-pulse text-[#1A237E] dark:text-[#E3F2FD] font-bold">Loading...</div></div>;

  const completedCount = progress ? Object.values(progress.missions || {}).filter(m => m.completed).length : 0;
  const totalMissions = season?.missions?.length || 6;
  const allComplete = completedCount >= totalMissions;

  return (
    <div className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] p-4 md:p-8" id="main-content" role="main" tabIndex={-1} data-testid="season-pass-page">
      <div className="max-w-2xl mx-auto">
        {/* Two-row header — back+menu on top, page title below — so
            the title has space to breathe. The hero's "Bull Run
            Season" heading is the SEASON name; this header names the
            PAGE so the user knows where they are before the hero
            loads. */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("common.back")}
          </Button>
          <AccountNavMenu current="season" />
        </div>
        <h1
          className="text-2xl md:text-3xl font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-2 mb-6"
          style={{ fontFamily: "Fredoka" }}
        >
          <Award className="w-6 h-6 text-[#FFC107] shrink-0" />
          {t("lobby.nav.season")}
        </h1>

        {/* Season Header */}
        {season && (
          <div className="rounded-3xl p-6 mb-6 text-white text-center"
            style={{ background: `linear-gradient(135deg, ${season.theme_color}CC, ${season.theme_color})` }}
            data-testid="season-header">
            <Award className="w-10 h-10 mx-auto mb-2 opacity-90" />
            <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: "Fredoka" }}>{season.name}</h1>
            <p className="text-sm opacity-80">{season.description}</p>
            <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
              <Badge className="bg-white/20 dark:bg-slate-900/20 text-white">{completedCount}/{totalMissions} Missions</Badge>
              {allComplete && <Badge className="bg-[#FFC107] text-[#1A237E] dark:text-[#E3F2FD]"><Unlock className="w-3 h-3 mr-1" /> Card Back Unlocked!</Badge>}
            </div>
            <div className="mt-3 flex justify-center">
              <SeasonCountdown />
            </div>
          </div>
        )}

        {/* Missions */}
        {season && progress && (
          <div className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-2" style={{ fontFamily: "Fredoka" }}>
                <Star className="w-5 h-5 text-[#FFC107]" /> Season Missions
              </h2>
              <span className="text-xs font-semibold text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{completedCount}/{totalMissions} done</span>
            </div>
            <div className="space-y-3">
              {season.missions.map((m) => {
                const mp = progress.missions?.[m.id] || { progress: 0, completed: false };
                const pct = Math.min(100, (mp.progress / m.target) * 100);
                return (
                  <div key={m.id}
                    className={`rounded-2xl p-4 border-2 transition-all ${
                      mp.completed
                        ? "bg-[#00E676]/10 border-[#00E676]/40"
                        : "bg-white/50 dark:bg-slate-900/50 border-[#1A237E]/10 dark:border-[#E3F2FD]/10"
                    }`}
                    data-testid={`season-mission-${m.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {mp.completed ? <Check className="w-4 h-4 text-[#00C853] shrink-0" /> : <Zap className="w-4 h-4 text-[#1A237E]/40 dark:text-[#E3F2FD]/40 shrink-0" />}
                        <span className="text-sm font-semibold text-[#1A237E] dark:text-[#E3F2FD] truncate">{m.label}</span>
                      </div>
                      <span className="text-xs font-bold shrink-0 ml-2" style={{ color: mp.completed ? "#00C853" : season.theme_color }}>+{m.xp_reward} XP</span>
                    </div>
                    <div className="h-2 w-full bg-[#1A237E]/10 dark:bg-[#E3F2FD]/10 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${pct}%`, background: mp.completed ? "#00E676" : season.theme_color }}
                      />
                    </div>
                    <p className="text-[10px] text-[#1A237E]/50 dark:text-[#E3F2FD]/50 mt-1.5 font-semibold">{mp.completed ? "Completed" : `${mp.progress} / ${m.target}`}</p>
                  </div>
                );
              })}
            </div>
            {progress.total_xp_earned > 0 && (
              <div className="mt-4 text-center text-sm font-bold" style={{ color: season.theme_color }}>
                Total Season XP: +{progress.total_xp_earned}
              </div>
            )}
          </div>
        )}

        {/* Card Backs */}
        <div className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6">
          <h2 className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-4" style={{ fontFamily: "Fredoka" }}>
            <Palette className="w-5 h-5 inline mr-2 text-[#8247E5]" /> Card Backs
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {cardBacks.map((cb) => {
              const rotationLabel =
                cb.availability === "current"
                  ? "Available this month"
                  : cb.availability === "future"
                  ? `Returns in ${cb.available_in}`
                  : cb.availability === "past"
                  ? `Was featured in ${cb.available_in}`
                  : null;
              return (
                <div key={cb.id}
                  className={`relative rounded-2xl p-4 text-center cursor-pointer transition-all border-2 ${
                    activeBack === cb.id ? "border-[#38B6FF] shadow-lg scale-105" :
                    cb.unlocked ? "border-[#1A237E]/10 dark:border-[#E3F2FD]/10 hover:border-[#38B6FF]/50" :
                    "border-[#1A237E]/5 dark:border-[#E3F2FD]/5 opacity-60"
                  }`}
                  style={{ background: cb.unlocked ? `linear-gradient(135deg, ${cb.color}20, ${cb.color}40)` : "#f0f0f0" }}
                  onClick={() => cb.unlocked && setCardBack(cb.id)}
                  data-testid={`card-back-${cb.id}`}>
                  {!cb.unlocked && <Lock className="absolute top-2 right-2 w-4 h-4 text-[#1A237E]/30 dark:text-[#E3F2FD]/30" />}
                  {cb.availability === "current" && (
                    <Badge className="absolute top-2 left-2 text-[8px] bg-[#FF5A5F] text-white">Now</Badge>
                  )}
                  {!cb.unlocked && cb.availability === "future" && (
                    <Badge className="absolute top-2 left-2 text-[8px] bg-[#8247E5] text-white">Coming Soon</Badge>
                  )}
                  <div className="w-12 h-16 mx-auto rounded-lg mb-2 border-2"
                    style={{ background: cb.unlocked ? `linear-gradient(135deg, ${cb.color}, ${cb.color}CC)` : "#ccc", borderColor: cb.color + "40" }} />
                  <p className="text-xs font-bold text-[#1A237E] dark:text-[#E3F2FD]">{cb.name}</p>
                  {activeBack === cb.id && <Badge className="mt-1 text-[8px] bg-[#38B6FF] text-white">Active</Badge>}
                  {!cb.unlocked && rotationLabel && (
                    <p className="text-[9px] text-[#1A237E]/40 dark:text-[#E3F2FD]/40 mt-1">{rotationLabel}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
